import {ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AiEventsService} from './ai-events.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SubscriptionUtils} from '../../commons/subscription.utils';
import {NotificationService} from '../../notification/notification.service';
import {VehiclesAiEventsResponse} from './model/vehicles-ai-events.response';
import {AiEventEntry} from './model/ai-event.entry';
import {LatestVehicleEntry} from "../latest-vehicles-info/model/latest-vehicle-entry";
import {FindMessageEntry} from "./model/find-message.entry";
import {ChatService} from "../chat/chat.service";
import {ChatVehicleListEntry} from "../chat/chat-vehicle-list.entry";
import {TranslateService} from "@ngx-translate/core";
import {ChatMessagesService} from "../chat/messages/chat-messages.service";
import {GlobalConstants} from "../../global/global.constants";
import {ChatUtils} from "../chat/chat.utils";
import {KeyboardCommands} from "../../../pages/client/settings/global-config/chat-configs/model/keyboard-commands";
import {AuthStorage} from "../../auth/auth-storage";
import {SendMessageEntry} from "./model/send-message.entry";
import {now} from "moment";
import {UndoStatusRequestEntry} from "./model/undo-status-request.entry";
import {Params, Router} from "@angular/router";
import {
  ProTasksQueueStateService
} from "../../../pages/client/tasks/pro-tasks-manager/queue/pro-tasks-queue-state.service";
import {AiEventsUtils} from "./ai-events.utils";

@Component({
  selector: 'app-ai-events-window',
  templateUrl: './ai-events.component.html',
  styleUrls: ['./ai-events.component.scss']
})
export class AiEventsComponent implements OnInit, OnDestroy {

  @ViewChild('upload', {static: true}) upload: ElementRef;

  private readonly intervalMs: number = 30000; //30 seconds

  public events: VehiclesAiEventsResponse[] = [];

  private aiEventsState = 'out';
  private chatState = 'out';
  private subSubject = new Subject<boolean>();
  private intervalId: any;
  private selectedVehicleId: number;
  private lastUpdateId: number = null;

  public newMessageFlag = false;
  public lastSeenAiEvent: number;
  public throttle = 500;
  public scrollUpDistance = 3;
  public scrollDownDistance = 3;
  public loadLock = true;
  public scrollDisabled = false;

  public vehicle: LatestVehicleEntry;
  public chatText = '';
  public file: any;
  public enterSelection = AuthStorage.getChatEnterConfigs();
  public shiftEnterSelection = AuthStorage.getChatShiftEnterConfigs();

  constructor(private aiEventsService: AiEventsService,
              private notificationService: NotificationService,
              private chatService: ChatService,
              private cd: ChangeDetectorRef,
              private translate: TranslateService,
              private router: Router,
              private chatMessagesService: ChatMessagesService,
              private queueStateService: ProTasksQueueStateService) {
  }

  public ngOnInit(): void {
    this.aiEventsService.aiEventsState$
      .pipe(takeUntil(this.subSubject))
      .subscribe({
        next: (state) => this.aiEventsState = state,
        error: () => this.notificationService.loadingError()
      });

    this.aiEventsState = this.chatService.getCurrentState();

    this.chatService.chatState$
      .pipe(takeUntil(this.subSubject))
      .subscribe({
        next: (state) => this.chatState = state,
        error: () => this.notificationService.loadingError()
      });

    this.chatState = this.chatService.getCurrentState();

    this.aiEventsService.stopAiEvents$
      .pipe(takeUntil(this.subSubject))
      .subscribe((data) => {
        if (this.intervalId != null) {
          this.stopInterval();
        }
      });

    this.aiEventsService.openAiEvents()
      .pipe(takeUntil(this.subSubject))
      .subscribe({
        next: (vehicle: LatestVehicleEntry) => this.openAiEventsForVehicle(vehicle),
        error: () => this.notificationService.loadingError()
      });
  }

  private openAiEventsForVehicle(vehicle: LatestVehicleEntry) {
    this.aiEventsState = this.aiEventsService.getCurrentState();
    this.vehicle = vehicle;
    let vehicleId = vehicle.id;
    if (vehicleId !== this.selectedVehicleId) {
      this.setSelectedVehicleId(vehicleId);
      this.startInterval(vehicleId);
      this.showAiEventsHideChat()
    } else {
      if (this.aiEventsState === 'out') {
        this.startInterval(vehicleId);
        this.showAiEventsHideChat();
      } else {
        this.aiEventsState = 'out';
        this.aiEventsService.changeState(this.aiEventsState);
        this.stopInterval();
      }
    }
    this.addGlobalClass()
  }

  private showAiEventsHideChat(): void {
    this.chatState = 'out';
    this.chatService.changeState(this.chatState);
    this.aiEventsState = 'in';
    this.aiEventsService.changeState(this.aiEventsState);
  }

  private setSelectedVehicleId(vehicleId: number): void {
    this.selectedVehicleId = this.selectedVehicleId === vehicleId ? null : vehicleId;
  }

  private startInterval(vehicleId: number): void {
    this.loadLock = true;
    this.stopInterval();
    this.events = [];
    this.aiEventsService.fetchVehiclesAIEvents(vehicleId).subscribe({
      next: (data) => {
        this.addEvents(data);
        this.lastSeenAiEvent = Math.max(...data.map(it => it.rootId));
        ChatUtils.scrollDown('events__list');
        this.unlockLoad();
        this.scrollDisabled = false;
      },
      error: () => {
        this.unlockLoad();
        this.notificationService.loadingError();
      }
    });
    this.intervalId = setInterval(() => this.updateAiEvents(), this.intervalMs);
  }

  private unlockLoad(): void {
    setTimeout(() => this.loadLock = false, 400);
  }

  private stopInterval(): void {
    clearInterval(this.intervalId);
    this.intervalId = null;
  }

  protected getEventClass(data: AiEventEntry): string {
    let postFix = data.revertedAt == null ? 'event--attention' : 'event--error';
    if (data.actionType === 'TASK') {
      if (data.detailedActionType.includes("STATUS_")) {
        return 'event--TASK_STATUS ' + postFix;
      }
      if (data.detailedActionType.includes("PLAN_")) {
        return 'event--TASK_PLAN' + postFix;
      }
      if (data.detailedActionType.includes('ROAD_CLOSURE')) {
        return 'event--ROAD_CLOSURE';
      }
      if (data.detailedActionType.includes('TRAFFIC')) {
        return 'event--TRAFFIC';
      }
      if (data.detailedActionType.includes('DELAY')) {
        return 'event--DELAY';
      }
    }
    if (data.actionType === 'MESSAGE') {
      if (data.detailedActionType === 'VOICE_TRANSCRIPTION') {
        return 'event--VOICE_TRANSCRIPTION event--success';
      }
      if (data.detailedActionType === 'MESSAGE_TRANSLATION') {
        return 'event--MESSAGE_TRANSLATION event--success';
      }
      if (data.detailedActionType === 'MESSAGE_SENT') {
        return 'event--MESSAGE_SENT';
      }
    }
    if (data.actionType === 'FORM') {
      if (data.detailedActionType === 'CMR_VALIDATION') {
        return 'event--DOCUMENT_PARSE ' + postFix;
      }
    }
    return '';
  }

  protected getEventTypeTranslation(data: AiEventEntry): string {
    if (data.actionType === 'TASK') {
      if (data.detailedActionType.includes("STATUS_")) {
        return this.translate.instant('ai-events.aiEventEntry.actionType.STATUS');
      }
      if (data.detailedActionType.includes("PLAN_")) {
        return this.translate.instant('ai-events.aiEventEntry.actionType.PLAN');
      }
    }

    return this.translate.instant('ai-events.aiEventEntry.actionType.' + data.detailedActionType);
  }

  protected closeAiEvents(): void {
    this.aiEventsState = 'out';
    this.aiEventsService.changeState(this.aiEventsState);
    this.selectedVehicleId = null;
    this.scrollDisabled = true;
    this.stopInterval();
    this.addGlobalClass();
  }

  private addGlobalClass(): void {
    let bodyClassList = document.body.classList;
    if (this.aiEventsState === 'in') {
      if (bodyClassList.contains('main--chat')) {
        bodyClassList.remove('main--chat');
      }
      if (bodyClassList.contains('main--zyggio')) {
        bodyClassList.remove('main--zyggio');
      }
      if (!bodyClassList.contains('main--events')) {
        bodyClassList.add('main--events');
      }
    } else if (this.aiEventsState === 'out') {
      bodyClassList.remove('main--events');
    }
  }

  private updateAiEvents(): void {
    this.aiEventsService.updateAiEvents(
      this.selectedVehicleId,
      this.lastSeenAiEvent)
      .pipe(takeUntil(this.subSubject))
      .subscribe(
        (data) => {
          if (data != null && data.newAiEvents.length > 0) {
            this.lastSeenAiEvent = data.lastSeenAiEvent;
            data.newAiEvents.sort((m1, m2) => m1.rootId > m2.rootId ? 1 : -1).forEach(d => {
              if (!this.events.includes(d)) {
                this.events.push(d);
                this.newMessageFlag = true;
              }
            });
            this.cd.markForCheck();
          }
        }
      )
  }

  public addAiEvents(scrolledUp: boolean): void {
    if (!scrolledUp) {
      this.newMessageFlag = false;
    }
    if (!this.loadLock && this.events.length > 0) {
      let lastAiEvent = this.getLastAiEvent(scrolledUp);
      this.aiEventsService.addAiEvents(this.selectedVehicleId, lastAiEvent, scrolledUp)
        .pipe(takeUntil(this.subSubject))
        .subscribe(
          (data) => {
            this.addEvents(data, scrolledUp);
            this.cd.markForCheck();
          }
        )
    }
  }

  private addEvents(data: VehiclesAiEventsResponse[], scrolledUp?: boolean) {
    data = scrolledUp ? data.reverse() : data;
    data.forEach(d => {
      if (this.events.filter(m => m.rootId === d.rootId).length === 0) {
        if (scrolledUp) {
          this.events.unshift(d)
        } else {
          this.events.push(d);
        }
      }
    });
  }

  private getLastAiEvent(scrolledUp: boolean): number {
    if (scrolledUp) {
      return this.events[0] != null ? this.events[0].rootId : -1;
    } else {
      return this.events[this.events.length - 1].rootId;
    }
  }

  public scrollToBottom(): void {
    this.newMessageFlag = false;
    ChatUtils.scrollDown('events__list');
  }

  public findAndScrollTo(event: VehiclesAiEventsResponse): void {
    this.scrollDisabled = true;
    let findMessageEntry = new FindMessageEntry(
      event.triggerEventId,
      event.messageSentOn
    )
    let chatVehicleListEntry = this.formChatVehicleListEntry(findMessageEntry);
    this.chatService.openChatLoadFiltered(chatVehicleListEntry);
  }

  public reviewForm(event: VehiclesAiEventsResponse): void {
    let params: Params = {
      page: 1,
      pageSize: 20
    };
    this.router.navigate([`/documents/vehicle-change/${event.triggerEventId}`], {queryParams: params});
  }

  public openTasks(): void {
    this.aiEventsService.initOpenTasks(this.vehicle);
  }

  public revert(entry: AiEventEntry, reason: string): void {
    let request = new UndoStatusRequestEntry(
      entry.eventId,
      entry.objectId,
      reason,
      entry.actionType,
      entry.detailedActionType
    );
    this.aiEventsService.undoStatus(this.selectedVehicleId, request)
      .subscribe({
        next: () => {
          entry.revertedAt = now().toString();
          this.notificationService.updatingSuccess();
          this.reloadTaskQueue();
        },
        error: () => this.notificationService.storingError()
      });
  }

  private reloadTaskQueue(): void {
    if (this.router.url.includes('/' + this.selectedVehicleId + '/manager/pro')) {
      this.queueStateService.reloadQueue(true);
    }
  }

  public getTime(event: VehiclesAiEventsResponse): string {
    let now = new Date();
    let input = new Date(Date.parse(event.triggerEventCreatedAt));
    const diffMs = now.getTime() - input.getTime();

    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 60) {
      return this.translate.instant('ai-events.minutesAgo', {value: diffMins});
    }

    const isToday =
      now.getFullYear() === input.getFullYear() &&
      now.getMonth() === input.getMonth() &&
      now.getDate() === input.getDate();

    if (isToday) {
      const hours = input.getHours().toString().padStart(2, '0');
      const minutes = input.getMinutes().toString().padStart(2, '0');
      return this.translate.instant('calendar.header.today') + ` ${hours}:${minutes}`;
    }
    return this.formatDate(input);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  public addChatItem(): void {
    let chatVehicleListEntry = this.formChatVehicleListEntry();
    let sendMessageEntry = new SendMessageEntry(
      chatVehicleListEntry,
      this.chatText,
      this.file
    );
    this.chatMessagesService.initSendMessage(sendMessageEntry);
    this.removeFile();
    this.chatText = '';
  }

  private formChatVehicleListEntry(findMessageEntry?: FindMessageEntry): ChatVehicleListEntry {
    return new ChatVehicleListEntry(
      this.vehicle.id,
      this.vehicle.plateNumber,
      this.vehicle.driver ? this.vehicle.driver.value : null,
      !this.vehicle.warnings.tabOffline,
      findMessageEntry != null ? findMessageEntry : null,
      this.vehicle.driverLanguage,
    );
  }

  public onPaste(event: any): void {
    const items = (event.clipboardData || event.originalEvent.clipboardData).items;
    for (const item of items) {
      if (item.type.indexOf('image') === 0) {
        event.preventDefault();
        let blob = item.getAsFile();
        if (blob !== null && blob.size < GlobalConstants.UPLOAD_FILE_LIMIT) {
          this.file = blob;
          this.resize();
        } else {
          this.removeFile();
          this.notificationService.fileLimitExceeded();
        }
      }
      if (item.type.indexOf('text') === 0) {
        let textarea = document.getElementById('text-m-input');
        textarea.style.height = '399px';
      }
    }
  }

  public onKeyShiftEnter(event: any): void {
    if (this.shiftEnterSelection === KeyboardCommands.ENTER) {
      event.preventDefault()
      this.addChatItem();
    }
  }

  public onKeyEnter(event: any): void {
    if (this.enterSelection === KeyboardCommands.ENTER) {
      event.preventDefault()
      this.addChatItem();
    }
  }

  public fileChange(upload) {
    if (upload.files.length && upload.files[0].size < GlobalConstants.UPLOAD_FILE_LIMIT) {
      this.file = upload.files[0];
      this.resize();
    } else {
      this.removeFile();
      this.notificationService.fileLimitExceeded();
    }
  }

  public removeFile(): void {
    this.file = null;
    this.upload.nativeElement.value = '';
    this.resize();
  }

  private resize() {
    setTimeout(() => {
      AiEventsUtils.resize('events__list', 'text-e-input', 'text-e-buttons', 'upload-e-file');
    }, 0);
  }

  public ngOnDestroy(): void {
    this.stopInterval();
    SubscriptionUtils.safeUnsubscribeSubject(this.subSubject);
  }
}
